/**
 * AudioComparatorEngine — A/B audio comparison engine.
 *
 * Pure-JS DSP for /audio-comparator. M1 surface area:
 *   - probeContainer(file) — magic-byte container/codec sniff (no decode needed)
 *   - quickSummary(probeA, bufA, probeB, bufB) — basic side-by-side table data
 *   - skeletonVerdict(summary) — placeholder verdict before real engine lands in M6
 *
 * Future milestones add: PSD/cutoff (M2), spectrograms (M3), xcorr alignment + sample diff (M4),
 * stereo metrics (M5), full weighted verdict + confidence (M6), ABX (M7), Deep stem mode (M8).
 *
 * Decode is NOT the engine's job — caller passes already-decoded AudioBuffers (decoded via
 * window.AudioDecoderRobust.robust). Keeps engine pure: input AudioBuffer in, JSON out.
 *
 * Worker-ready: every public method takes plain data (File, AudioBuffer, Float32Array) and
 * returns plain JSON. M4 will spawn audio-comparator-worker.js that imports this engine via
 * importScripts() and does the heavy xcorr/FFT off the main thread.
 */
(function (global) {
'use strict';

var VERSION = '0.11.0-QA1';

/* ──────────────────────────────────────────────────────────────────
 * Container / codec probe (Tier 1 — instant, no decode)
 *
 * Reads first 64 bytes of the file and matches magic-byte signatures.
 * Returns container + best-effort codec guess + extension match check.
 * For tools where we want bitrate / encoder fingerprint, the M2 wrapper
 * will call FFmpegProcessor for a fuller probe; this is the fast path.
 * ────────────────────────────────────────────────────────────────── */

function probeContainer(file) {
    return new Promise(function (resolve) {
        if (!file || typeof file.size !== 'number' || file.size < 16) {
            return resolve({ container: 'unknown', codec: null, ext: null, extMatch: null, size: 0 });
        }
        var ext = (file.name || '').split('.').pop().toLowerCase();
        var head = file.slice(0, 64);
        var fr = new FileReader();
        fr.onerror = function () {
            resolve({ container: 'unknown', codec: null, ext: ext, extMatch: null, size: file.size, name: file.name, type: file.type || null });
        };
        fr.onload = function () {
            var bytes = new Uint8Array(fr.result);
            var info = sniffMagic(bytes);
            resolve({
                container: info.container,
                codec: info.codec,
                ext: ext,
                extMatch: info.container === 'unknown' ? null : matchesExt(info.container, ext),
                size: file.size,
                name: file.name,
                type: file.type || null,
            });
        };
        fr.readAsArrayBuffer(head);
    });
}

function sniffMagic(b) {
    if (b.length < 8) return { container: 'unknown', codec: null };
    // RIFF/WAVE
    if (b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46 &&
        b[8] === 0x57 && b[9] === 0x41 && b[10] === 0x56 && b[11] === 0x45) {
        return { container: 'wav', codec: 'pcm' };
    }
    // fLaC
    if (b[0] === 0x66 && b[1] === 0x4C && b[2] === 0x61 && b[3] === 0x43) {
        return { container: 'flac', codec: 'flac' };
    }
    // OggS
    if (b[0] === 0x4F && b[1] === 0x67 && b[2] === 0x67 && b[3] === 0x53) {
        // Could be vorbis or opus; peek into first 32 bytes of next page would tell us
        return { container: 'ogg', codec: null };
    }
    // ID3v2 → MP3 (definitive)
    if (b[0] === 0x49 && b[1] === 0x44 && b[2] === 0x33) {
        return { container: 'mp3', codec: 'mp3' };
    }
    // ADTS (AAC raw stream) sync — 12-bit sync 0xFFF + layer=00 + protection bit.
    // First byte 0xFF, second byte has top nibble 0xF (sync), then bits:
    //   bit 4 (mpeg version, 0=MPEG-4, 1=MPEG-2): either OK
    //   bits 2-3 = 00 (layer, always 0 for ADTS — same as MPEG layer III but...)
    //   bit 0 = protection absence (1 = no CRC)
    // Common ADTS second bytes: 0xF1 (MPEG-4, no CRC), 0xF9 (MPEG-2, no CRC),
    //                            0xF0 (MPEG-4, CRC), 0xF8 (MPEG-2, CRC).
    // MPEG audio (MP1/2/3) frame sync also starts 0xFF but layer bits differ:
    //   MP3 layer III: bits 2-3 = 01 → second byte 0xFA, 0xFB, 0xF2, 0xF3, 0xE2, 0xE3
    //   MP2 layer II:  bits 2-3 = 10 → second byte 0xFC, 0xFD, 0xF4, 0xF5
    //   MP1 layer I:   bits 2-3 = 11 → second byte 0xFE, 0xFF, 0xF6, 0xF7
    // So we discriminate on those layer bits.
    if (b[0] === 0xFF && (b[1] & 0xF0) === 0xF0) {
        var layerBits = (b[1] >> 1) & 0x03;  // 00=AAC ADTS, 01=MP3, 10=MP2, 11=MP1
        if (layerBits === 0x00) return { container: 'aac', codec: 'aac' };
        if (layerBits === 0x01) return { container: 'mp3', codec: 'mp3' };
        if (layerBits === 0x02) return { container: 'mp3', codec: 'mp2' }; // mp2 — uncommon, share container
        if (layerBits === 0x03) return { container: 'mp3', codec: 'mp1' };
    }
    // MPEG sync without strict-F0 prefix (covers older 0xE0 mask variants)
    if (b[0] === 0xFF && (b[1] & 0xE0) === 0xE0) {
        return { container: 'mp3', codec: 'mp3' };
    }
    // ISO Base Media (M4A / MP4 / MOV) — "ftyp" at offset 4
    if (b.length >= 12 && b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70) {
        // Brand at offset 8..11
        var brand = String.fromCharCode(b[8], b[9], b[10], b[11]);
        var codec = (brand === 'M4A ' || brand === 'mp42' || brand === 'isom' || brand === 'M4B ') ? 'aac' : null;
        return { container: brand.trim() === 'M4B' ? 'm4b' : (brand.indexOf('mp4') === 0 || brand === 'isom' ? 'mp4' : 'm4a'), codec: codec };
    }
    // EBML (WebM / Matroska)
    if (b[0] === 0x1A && b[1] === 0x45 && b[2] === 0xDF && b[3] === 0xA3) {
        return { container: 'webm', codec: null };
    }
    // AIFF — FORM ... AIFF
    if (b[0] === 0x46 && b[1] === 0x4F && b[2] === 0x52 && b[3] === 0x4D &&
        b[8] === 0x41 && b[9] === 0x49 && b[10] === 0x46 && b[11] === 0x46) {
        return { container: 'aiff', codec: 'pcm' };
    }
    return { container: 'unknown', codec: null };
}

function matchesExt(container, ext) {
    if (!ext) return null;
    var map = {
        wav: ['wav', 'wave'],
        flac: ['flac'],
        ogg: ['ogg', 'oga', 'opus'],
        mp3: ['mp3', 'mpeg', 'mp2'],
        aac: ['aac', 'adts'],
        m4a: ['m4a', 'aac'],
        m4b: ['m4b'],
        mp4: ['mp4', 'm4v'],
        webm: ['webm'],
        aiff: ['aiff', 'aif', 'aifc'],
    };
    var allowed = map[container] || [];
    for (var i = 0; i < allowed.length; i++) {
        if (allowed[i] === ext) return true;
    }
    return false;
}

/* ──────────────────────────────────────────────────────────────────
 * Tier 2 — Decoded PCM basic stats (just what M1 needs for the summary table)
 * Heavier metrics (LUFS, true peak, cutoff, spectrum) land in M2+.
 * ────────────────────────────────────────────────────────────────── */

function basicStats(buf) {
    if (!buf || typeof buf.length !== 'number') return null;
    var nCh = buf.numberOfChannels;
    var sr = buf.sampleRate;
    var len = buf.length;
    var duration = buf.duration;

    // Peak and RMS sample-domain
    var peak = 0;
    var sumSq = 0;
    var totalSamples = 0;
    for (var c = 0; c < nCh; c++) {
        var ch = buf.getChannelData(c);
        for (var i = 0; i < len; i++) {
            var v = Math.abs(ch[i]);
            if (v > peak) peak = v;
            sumSq += ch[i] * ch[i];
            totalSamples++;
        }
    }
    var rms = totalSamples > 0 ? Math.sqrt(sumSq / totalSamples) : 0;

    // Estimated bitrate from file size (caller passes), not computable here without context
    return {
        sampleRate: sr,
        channels: nCh,
        durationSec: duration,
        samples: len,
        peak: peak,
        peakDbfs: peak > 0 ? 20 * Math.log10(peak) : -Infinity,
        rms: rms,
        rmsDbfs: rms > 0 ? 20 * Math.log10(rms) : -Infinity,
        crestFactor: rms > 0 ? peak / rms : 0,
    };
}

/* ──────────────────────────────────────────────────────────────────
 * M2 — Spectrum / cutoff / loudness pipeline.
 *
 * computeAveragePsd  — Welch's-method PSD averaged across all frames of the
 *                     file. Hann window, 50 % overlap. Returns dB values
 *                     at log-spaced frequency bins for the overlay canvas
 *                     plus the raw linear-frequency array for cutoff math.
 *
 * findSpectralCutoff — heuristic "lossy ceiling" detector. Scans down from
 *                     Nyquist looking for the highest freq where a 500 Hz
 *                     window's average PSD first crosses above the noise
 *                     floor + threshold. Returns Hz, plus a confidence
 *                     [0..1] flag based on how steep the drop-off is.
 *
 * measureLoudness    — wraps LoudnessR128.measureDetailed; returns
 *                     {integratedLufs, lra, truePeakDb, momentaryMax,
 *                      shortTermMax, peakDb, rmsDb}. Falls back to
 *                     basicStats peak/rms if LoudnessR128 isn't loaded.
 * ────────────────────────────────────────────────────────────────── */

// Radix-2 in-place Cooley-Tukey FFT. re/im are Float32Array of equal length n=2^k.
function fftRadix2(re, im) {
    var n = re.length;
    for (var i = 1, j = 0; i < n; i++) {
        var bit = n >> 1;
        for (; j & bit; bit >>= 1) j ^= bit;
        j ^= bit;
        if (i < j) {
            var tr = re[i]; re[i] = re[j]; re[j] = tr;
            var ti = im[i]; im[i] = im[j]; im[j] = ti;
        }
    }
    for (var len = 2; len <= n; len <<= 1) {
        var ang = -2 * Math.PI / len;
        var wlr = Math.cos(ang), wli = Math.sin(ang);
        for (var i2 = 0; i2 < n; i2 += len) {
            var wr = 1, wi = 0;
            for (var k = 0; k < (len >> 1); k++) {
                var a = i2 + k, b = i2 + k + (len >> 1);
                var tre = wr * re[b] - wi * im[b];
                var tim = wr * im[b] + wi * re[b];
                re[b] = re[a] - tre; im[b] = im[a] - tim;
                re[a] += tre;        im[a] += tim;
                var nwr = wr * wlr - wi * wli;
                var nwi = wr * wli + wi * wlr;
                wr = nwr; wi = nwi;
            }
        }
    }
}

// Hann window weights for the given size. Cached per-size so 4096-sized
// runs across many PSD blocks reuse one allocation.
var _hannCache = {};
function hannWindow(n) {
    if (_hannCache[n]) return _hannCache[n];
    var w = new Float32Array(n);
    for (var i = 0; i < n; i++) w[i] = 0.5 * (1 - Math.cos(2 * Math.PI * i / (n - 1)));
    return (_hannCache[n] = w);
}

/**
 * Welch's-method PSD over the entire buffer.
 *
 * Strategy:
 *   - Down-mix to mono by averaging channels
 *   - Hann-window blocks of fftSize with 50 % overlap
 *   - |FFT|² of each block, accumulate per-bin sum
 *   - Average → divide by (fftSize · sum(w²)) → power-spectral-density
 *   - Convert to dB (10·log10 with a tiny floor to avoid -Inf)
 *
 * Returns:
 *   {
 *     freqHz:     Float32Array[fftSize/2]   — frequency of each bin (Hz)
 *     psdDb:      Float32Array[fftSize/2]   — average PSD in dB
 *     fftSize:    used fftSize
 *     blocks:     number of FFT blocks averaged
 *     sampleRate: source sr
 *   }
 *
 * fftSize must be a power of 2. Default 8192 gives 5.86 Hz bin spacing at
 * 48 kHz — plenty fine for cutoff detection and an overlay plot.
 */
function computeAveragePsd(buffer, opts) {
    opts = opts || {};
    var fftSize = opts.fftSize || 8192;
    // round up to next power of 2
    var n = 1; while (n < fftSize) n <<= 1;
    fftSize = n;

    if (!buffer || buffer.length < fftSize) {
        return { freqHz: new Float32Array(0), psdDb: new Float32Array(0), fftSize: fftSize, blocks: 0, sampleRate: buffer ? buffer.sampleRate : 0 };
    }

    var sr = buffer.sampleRate;
    var nCh = buffer.numberOfChannels;
    var len = buffer.length;
    var halfFft = fftSize >> 1;
    var hop = fftSize >> 1; // 50 % overlap

    // Down-mix to mono into a Float32Array we can slice.
    // For typical 4-minute files this is a one-time ~64 MB allocation — acceptable.
    var mono = new Float32Array(len);
    if (nCh === 1) {
        mono.set(buffer.getChannelData(0));
    } else {
        var c0 = buffer.getChannelData(0);
        var c1 = buffer.getChannelData(1);
        for (var i = 0; i < len; i++) mono[i] = (c0[i] + c1[i]) * 0.5;
    }

    var win = hannWindow(fftSize);
    var winNormSq = 0;
    for (var iw = 0; iw < fftSize; iw++) winNormSq += win[iw] * win[iw];

    var psdAccum = new Float64Array(halfFft);
    var re = new Float32Array(fftSize);
    var im = new Float32Array(fftSize);
    var blocks = 0;

    for (var off = 0; off + fftSize <= len; off += hop) {
        // Window the block into re/im
        for (var k = 0; k < fftSize; k++) {
            re[k] = mono[off + k] * win[k];
            im[k] = 0;
        }
        fftRadix2(re, im);
        // |FFT|² and accumulate (DC..Nyquist-1 → halfFft bins)
        for (var b = 0; b < halfFft; b++) {
            psdAccum[b] += re[b] * re[b] + im[b] * im[b];
        }
        blocks++;
    }

    if (blocks === 0) {
        return { freqHz: new Float32Array(0), psdDb: new Float32Array(0), fftSize: fftSize, blocks: 0, sampleRate: sr };
    }

    var freqHz = new Float32Array(halfFft);
    var psdDb = new Float32Array(halfFft);
    var norm = 1 / (blocks * winNormSq * sr);
    var FLOOR = 1e-20;
    for (var f = 0; f < halfFft; f++) {
        freqHz[f] = f * sr / fftSize;
        // Single-sided PSD; multiply non-DC/non-Nyquist by 2 for one-sided convention.
        var pNorm = psdAccum[f] * norm;
        if (f > 0 && f < halfFft - 1) pNorm *= 2;
        psdDb[f] = 10 * Math.log10(Math.max(pNorm, FLOOR));
    }
    return { freqHz: freqHz, psdDb: psdDb, fftSize: fftSize, blocks: blocks, sampleRate: sr };
}

/**
 * Find spectral cutoff (lossy ceiling marker).
 *
 * Walks the PSD from Nyquist downward, computing the average dB inside a
 * sliding 500 Hz window. The "cutoff" is the highest frequency where this
 * window's average crosses ABOVE the noise floor + threshold. Empirically
 * this catches MP3 lowpass at 19-20 kHz, AAC at ~18 kHz, Vorbis at ~16 kHz,
 * 128k MP3 at ~16 kHz, etc.
 *
 * Noise floor = median of the bottom 10 % of PSD values (robust to spectral
 * peaks). Threshold = noise + 12 dB (= consistent with how Spek labels it).
 *
 * Returns:
 *   { hz:        cutoff frequency (or Nyquist if no cutoff found)
 *     isFullBand: true if cutoff is within 200 Hz of Nyquist
 *     noiseFloorDb: dB
 *     thresholdDb:  dB the cutoff window had to exceed }
 */
function findSpectralCutoff(psdResult) {
    if (!psdResult || !psdResult.psdDb || psdResult.psdDb.length === 0) {
        return { hz: 0, isFullBand: false, noiseFloorDb: -Infinity, thresholdDb: -Infinity };
    }
    var psd = psdResult.psdDb;
    var freq = psdResult.freqHz;
    var sr = psdResult.sampleRate;
    var nyq = sr / 2;
    var n = psd.length;

    // Noise floor: median of bottom 10 % (sorted lowest first → middle of bottom decile)
    var sortable = new Float32Array(psd);
    sortable.sort();
    var lowDecile = Math.max(1, Math.floor(n * 0.1));
    var noiseFloorDb = sortable[lowDecile >> 1];
    var thresholdDb = noiseFloorDb + 12;

    // Sliding 500 Hz window from Nyquist down. binsPerHz = n / nyq.
    var winHz = 500;
    var winBins = Math.max(4, Math.round((winHz / nyq) * n));

    var cutoffBin = n - 1; // default: full-band
    for (var b = n - 1; b >= winBins; b--) {
        var sum = 0;
        for (var k = 0; k < winBins; k++) sum += psd[b - k];
        var mean = sum / winBins;
        if (mean >= thresholdDb) {
            cutoffBin = b;
            break;
        }
    }
    var hz = freq[cutoffBin];
    var isFullBand = (nyq - hz) < 200; // within 200 Hz of Nyquist → no meaningful cutoff
    return { hz: hz, isFullBand: isFullBand, noiseFloorDb: noiseFloorDb, thresholdDb: thresholdDb };
}

/**
 * Short-term LUFS over time — for the overlay line chart.
 *
 * Uses LoudnessR128.createMeter (BS.1770-4 streaming meter) and pushes
 * samples in 100 ms hops. After each hop the meter's shortTermLUFS()
 * gives a smoothed loudness reading over the last 3 s window.
 *
 * Returns:
 *   {
 *     timeSec:    Float32Array — center-time of each sample (s)
 *     stLufs:     Float32Array — short-term LUFS at each hop (or -Inf for
 *                              gated silence)
 *     hopMs:      hop spacing
 *   }
 *
 * Falls back to {timeSec:[], stLufs:[]} when LoudnessR128 isn't loaded.
 */
function measureShortTermLufsOverTime(buffer, opts) {
    opts = opts || {};
    var hopMs = opts.hopMs || 100;
    var L = (typeof window !== 'undefined') ? window.LoudnessR128
          : (typeof self !== 'undefined' ? self.LoudnessR128 : null);
    if (!L || typeof L.createMeter !== 'function' || !buffer) {
        return { timeSec: new Float32Array(0), stLufs: new Float32Array(0), hopMs: hopMs };
    }
    try {
        var sr = buffer.sampleRate;
        var nCh = buffer.numberOfChannels;
        var len = buffer.length;
        var hopFrames = Math.max(1, Math.round(sr * hopMs / 1000));
        var numHops = Math.floor(len / hopFrames);
        if (numHops < 1) {
            return { timeSec: new Float32Array(0), stLufs: new Float32Array(0), hopMs: hopMs };
        }
        var meter = L.createMeter(sr, nCh);
        var time = new Float32Array(numHops);
        var st   = new Float32Array(numHops);
        // Pre-cache the channel Float32Arrays so we don't call getChannelData() per hop.
        var chans = new Array(nCh);
        for (var c = 0; c < nCh; c++) chans[c] = buffer.getChannelData(c);
        // Buffer one window of slices we hand to the meter each hop.
        var slices = new Array(nCh);
        for (var h = 0; h < numHops; h++) {
            var off = h * hopFrames;
            for (var ci = 0; ci < nCh; ci++) {
                slices[ci] = chans[ci].subarray(off, off + hopFrames);
            }
            meter.push(slices, hopFrames);
            time[h] = (off + hopFrames / 2) / sr;
            st[h]   = meter.shortTermLUFS();
        }
        return { timeSec: time, stLufs: st, hopMs: hopMs };
    } catch (e) {
        return { timeSec: new Float32Array(0), stLufs: new Float32Array(0), hopMs: hopMs };
    }
}

/* ──────────────────────────────────────────────────────────────────
 * M5 — Stereo metrics (M/S, phase correlation, width) + watermark scan.
 *
 *   computeStereoMetrics(buf) → {
 *     isMono:           true if input is single-channel
 *     phaseCorrelation: Lt/Rt cross-correlation [-1..+1]  (1 = identical,
 *                                                          0 = independent,
 *                                                          -1 = polarity-flipped)
 *     width:            side-energy fraction in [0..1] (0 = mono-sum, 1 = pure side)
 *     msRatio:          mid/side energy ratio (Inf when side ≈ 0)
 *     midRms / sideRms: RMS amplitudes (linear, 0..1-ish)
 *     midRmsDb / sideRmsDb: same in dBFS
 *   }
 *
 *   scanWatermark(file) — wraps window.AudioWatermarkDetector.scan with
 *                        a graceful fallback that returns {detected:false}
 *                        when the script hasn't loaded yet.
 * ────────────────────────────────────────────────────────────────── */

function computeStereoMetrics(buffer) {
    if (!buffer || typeof buffer.getChannelData !== 'function') {
        return null;
    }
    if (buffer.numberOfChannels < 2) {
        return { isMono: true, phaseCorrelation: NaN, width: 0, msRatio: Infinity, midRms: 0, sideRms: 0, midRmsDb: -Infinity, sideRmsDb: -Infinity };
    }
    var L = buffer.getChannelData(0);
    var R = buffer.getChannelData(1);
    var len = Math.min(L.length, R.length);
    if (len < 1) return null;

    var sumL = 0, sumR = 0;
    for (var i = 0; i < len; i++) { sumL += L[i]; sumR += R[i]; }
    var mL = sumL / len, mR = sumR / len;

    var sumLR = 0, sumLL = 0, sumRR = 0;
    var sumMidSq = 0, sumSideSq = 0;
    for (var j = 0; j < len; j++) {
        var l = L[j] - mL;
        var r = R[j] - mR;
        sumLR += l * r;
        sumLL += l * l;
        sumRR += r * r;
        var mid  = (L[j] + R[j]) * 0.5;
        var side = (L[j] - R[j]) * 0.5;
        sumMidSq  += mid * mid;
        sumSideSq += side * side;
    }
    var denom = Math.sqrt(sumLL * sumRR);
    var phaseCorr = denom > 0 ? (sumLR / denom) : 0;
    var midRms  = Math.sqrt(sumMidSq  / len);
    var sideRms = Math.sqrt(sumSideSq / len);
    var totalEnergy = sumMidSq + sumSideSq;
    var width = totalEnergy > 0 ? (sumSideSq / totalEnergy) : 0;
    var msRatio = sumSideSq > 0 ? (sumMidSq / sumSideSq) : Infinity;

    return {
        isMono: false,
        phaseCorrelation: phaseCorr,
        width: width,
        msRatio: msRatio,
        midRms: midRms,
        sideRms: sideRms,
        midRmsDb:  midRms  > 0 ? 20 * Math.log10(midRms)  : -Infinity,
        sideRmsDb: sideRms > 0 ? 20 * Math.log10(sideRms) : -Infinity,
    };
}

function scanWatermark(file) {
    var W = (typeof window !== 'undefined') ? window.AudioWatermarkDetector
          : (typeof self !== 'undefined' ? self.AudioWatermarkDetector : null);
    if (!W || typeof W.scan !== 'function' || W.__isStub) {
        return Promise.resolve({ detected: false, source: null, kind: null, confidence: 0, evidence: null, _fallback: true });
    }
    try {
        return Promise.resolve(W.scan(file)).catch(function () {
            return { detected: false, source: null, kind: null, confidence: 0, evidence: null, _error: true };
        });
    } catch (e) {
        return Promise.resolve({ detected: false, source: null, kind: null, confidence: 0, evidence: null, _error: true });
    }
}

/**
 * Wrap LoudnessR128 with a graceful fallback to basicStats peak/rms when
 * the script isn't loaded yet (defer race protection). Returns the full
 * detail object expected by the UI.
 */
function measureLoudness(buffer) {
    var L = (typeof window !== 'undefined') ? window.LoudnessR128
          : (typeof self !== 'undefined' ? self.LoudnessR128 : null);
    if (L && typeof L.measureDetailed === 'function') {
        try {
            var d = L.measureDetailed(buffer);
            return {
                integratedLufs: d.integratedLUFS,
                lra:            d.loudnessRangeLU,
                truePeakDb:     d.truePeakDb,
                momentaryMax:   d.momentaryMaxLUFS,
                shortTermMax:   d.shortTermMaxLUFS,
                peakDb:         d.peakDb,
                rmsDb:          d.rmsDb,
                source:         'loudness-r128',
            };
        } catch (e) {
            // fall through to basicStats fallback
        }
    }
    var basic = basicStats(buffer);
    return {
        integratedLufs: NaN, lra: NaN, truePeakDb: NaN,
        momentaryMax: NaN, shortTermMax: NaN,
        peakDb: basic ? basic.peakDbfs : -Infinity,
        rmsDb:  basic ? basic.rmsDbfs  : -Infinity,
        source: 'fallback',
    };
}

/* ──────────────────────────────────────────────────────────────────
 * M4 — Alignment, sample diff, spectrogram diff, bit-identical detection.
 *
 *   alignBuffers(bufA, bufB)
 *     Returns {offsetSec, correlation, sourceSampleRate, durationOverlap}.
 *     Down-samples both files to mono 4000 Hz and runs FFT-based cross-
 *     correlation on a 5-second middle slice. Offset is the sec to push B
 *     so it lines up with A. Correlation is normalized [-1..1] at the
 *     winning lag. Robust to volume mismatch (normalised cross-correlation).
 *
 *   computeSampleDiff(bufA, bufB, offsetSec)
 *     Aligned per-sample comparison. Returns
 *       { rmsPerSec: Float32Array,        // RMS of (A-B) per 1-second bucket
 *         maxAbsDiff:   number,            // worst-case sample delta
 *         totalDiffPct: number,             // % of samples differing > 2^-15
 *         bitIdentical: bool,               // peak diff < 2^-15
 *         durationSec:  number              // overlap window covered
 *       }
 *     Only call when align correlation > 0.85.
 *
 *   bitIdenticalQuick(bufA, bufB)
 *     Cheap early-exit: same channels/SR/length and first 1k samples
 *     match to 2^-20. For the common case of "two identical WAVs with
 *     different filenames" we want to skip xcorr entirely.
 *
 *   computeSpectrogramDiff(bufA, bufB, offsetSec, opts)
 *     Returns a 2D Uint8Array of |Adb-Bdb| pixels for the canvas, plus
 *     the timestamp of the brightest pixel column (for "hot moment"
 *     bookmarks). Lighter wrapper around the same FFT we use elsewhere.
 * ────────────────────────────────────────────────────────────────── */

// ─── helpers ──────────────────────────────────────────────────────

function toMono(buffer) {
    var nCh = buffer.numberOfChannels;
    var len = buffer.length;
    if (nCh === 1) return buffer.getChannelData(0);
    var c0 = buffer.getChannelData(0);
    var c1 = buffer.getChannelData(1);
    var out = new Float32Array(len);
    for (var i = 0; i < len; i++) out[i] = (c0[i] + c1[i]) * 0.5;
    return out;
}

// Simple linear-interp downsample. Adequate for cross-correlation alignment
// where we just need rough peak location; not for audio-quality work.
function downsample(samples, srcSr, dstSr) {
    if (srcSr === dstSr) return samples;
    var ratio = srcSr / dstSr;
    var dstLen = Math.floor(samples.length / ratio);
    var out = new Float32Array(dstLen);
    for (var i = 0; i < dstLen; i++) {
        var pos = i * ratio;
        var i0 = pos | 0;
        var frac = pos - i0;
        var s0 = samples[i0] || 0;
        var s1 = samples[i0 + 1] || s0;
        out[i] = s0 * (1 - frac) + s1 * frac;
    }
    return out;
}

// Quick bit-identical check: same shape and first N samples match to 2^-20.
// If this returns true we still run full diff to confirm — this is just the
// early-exit gate to skip xcorr alignment.
function bitIdenticalQuick(bufA, bufB) {
    if (!bufA || !bufB) return false;
    if (bufA.numberOfChannels !== bufB.numberOfChannels) return false;
    if (bufA.sampleRate !== bufB.sampleRate) return false;
    if (bufA.length !== bufB.length) return false;
    var nCh = bufA.numberOfChannels;
    var probe = Math.min(1024, bufA.length);
    var EPS = Math.pow(2, -20);
    for (var c = 0; c < nCh; c++) {
        var a = bufA.getChannelData(c);
        var b = bufB.getChannelData(c);
        for (var i = 0; i < probe; i++) {
            if (Math.abs(a[i] - b[i]) > EPS) return false;
        }
    }
    return true;
}

// FFT-based cross-correlation. Both inputs must be Float32Array. Returns
// {bestLag, correlation, lagAxis}. Lag is in samples; positive lag means
// B is delayed relative to A.
function fftCrossCorrelation(a, b) {
    // Zero-pad to next power-of-2 of 2*max(len)
    var maxLen = Math.max(a.length, b.length);
    var n = 1; while (n < 2 * maxLen) n <<= 1;

    var aRe = new Float32Array(n); var aIm = new Float32Array(n);
    var bRe = new Float32Array(n); var bIm = new Float32Array(n);

    // Subtract means to make the correlation robust to DC offset
    var ma = 0, mb = 0;
    for (var ia = 0; ia < a.length; ia++) ma += a[ia]; ma /= a.length || 1;
    for (var ib = 0; ib < b.length; ib++) mb += b[ib]; mb /= b.length || 1;
    for (var ja = 0; ja < a.length; ja++) aRe[ja] = a[ja] - ma;
    for (var jb = 0; jb < b.length; jb++) bRe[jb] = b[jb] - mb;

    fftRadix2(aRe, aIm);
    fftRadix2(bRe, bIm);

    // Cross-spectrum: A * conj(B)
    var cRe = new Float32Array(n);
    var cIm = new Float32Array(n);
    for (var k = 0; k < n; k++) {
        cRe[k] = aRe[k] * bRe[k] + aIm[k] * bIm[k];
        cIm[k] = aIm[k] * bRe[k] - aRe[k] * bIm[k];
    }
    // Inverse FFT — same as forward but conjugated, then scale by 1/n
    var cReI = new Float32Array(n);
    var cImI = new Float32Array(n);
    for (var ki = 0; ki < n; ki++) { cReI[ki] = cRe[ki]; cImI[ki] = -cIm[ki]; }
    fftRadix2(cReI, cImI);
    var inv = 1 / n;
    for (var kk = 0; kk < n; kk++) cReI[kk] *= inv;

    // Find max in two halves (positive vs negative lags wrap)
    var bestLag = 0, bestVal = -Infinity;
    var halfWindow = Math.min(a.length, b.length); // restrict to reasonable lag range
    for (var lag = -halfWindow; lag <= halfWindow; lag++) {
        var idx = lag >= 0 ? lag : (n + lag);
        var v = cReI[idx];
        if (v > bestVal) { bestVal = v; bestLag = lag; }
    }

    // Normalise: divide by sqrt(sumSq(a) * sumSq(b))
    var sa = 0, sb = 0;
    for (var ix = 0; ix < a.length; ix++) sa += (a[ix] - ma) * (a[ix] - ma);
    for (var iy = 0; iy < b.length; iy++) sb += (b[iy] - mb) * (b[iy] - mb);
    var denom = Math.sqrt(sa * sb);
    var correlation = denom > 0 ? (bestVal / denom) : 0;

    return { bestLag: bestLag, correlation: correlation };
}

// ─── alignBuffers — public ───────────────────────────────────────

function alignBuffers(bufA, bufB, opts) {
    opts = opts || {};
    if (!bufA || !bufB) {
        return { offsetSec: 0, correlation: 0, sourceSampleRate: 0, durationOverlap: 0 };
    }

    // 1. Bit-identical quick-exit: zero offset, perfect correlation.
    if (bitIdenticalQuick(bufA, bufB)) {
        return { offsetSec: 0, correlation: 1, sourceSampleRate: bufA.sampleRate, durationOverlap: bufA.duration, bitIdenticalQuick: true };
    }

    // 2. Down-sample both to mono 4000 Hz for fast xcorr
    var ANALYSIS_SR = 4000;
    var ANALYSIS_DURATION = 5; // seconds — analysis window from the middle of each file
    var monoA = toMono(bufA);
    var monoB = toMono(bufB);
    var dsA = downsample(monoA, bufA.sampleRate, ANALYSIS_SR);
    var dsB = downsample(monoB, bufB.sampleRate, ANALYSIS_SR);

    var winSamples = Math.min(ANALYSIS_DURATION * ANALYSIS_SR, dsA.length, dsB.length);
    if (winSamples < ANALYSIS_SR) {
        // Too short for a meaningful 1-sec correlation
        return { offsetSec: 0, correlation: 0, sourceSampleRate: bufA.sampleRate, durationOverlap: 0 };
    }
    var startA = Math.max(0, Math.floor((dsA.length - winSamples) / 2));
    var startB = Math.max(0, Math.floor((dsB.length - winSamples) / 2));
    var sliceA = dsA.subarray(startA, startA + winSamples);
    var sliceB = dsB.subarray(startB, startB + winSamples);

    var xc = fftCrossCorrelation(sliceA, sliceB);
    // fftCrossCorrelation convention: bestLag = L such that
    //   sliceA[t] ≈ sliceB[t − L]  for many t
    // Substituting sliceA[t] = dsA[startA+t] and sliceB[t-L] = dsB[startB+t-L]:
    //   dsA[startA + t] ≈ dsB[startB + t − L]
    // So position `u` in dsA maps to position `u + (startB − startA − L)` in dsB.
    // The user-facing "offset of B relative to A" is exactly this shift —
    // how much later B's content appears versus A's. SIGN: subtract bestLag.
    // (Previous version had `(bestLag - (startA - startB))` which is the same
    // magnitude but WRONG SIGN — caught by self-validator on prod 2026-05-22.)
    var offsetSec = ((startB - startA) - xc.bestLag) / ANALYSIS_SR;

    // Overlap duration at native rate
    var nativeOffsetSamplesA = Math.round(offsetSec * bufA.sampleRate);
    var overlapSamples = Math.min(bufA.length - Math.max(0, nativeOffsetSamplesA), bufB.length - Math.max(0, -nativeOffsetSamplesA));
    var durationOverlap = Math.max(0, overlapSamples) / bufA.sampleRate;

    return {
        offsetSec: offsetSec,
        correlation: xc.correlation,
        sourceSampleRate: bufA.sampleRate,
        durationOverlap: durationOverlap,
    };
}

// ─── computeSampleDiff — per-sample aligned comparison ─────────────

function computeSampleDiff(bufA, bufB, offsetSec) {
    if (!bufA || !bufB) {
        return { rmsPerSec: new Float32Array(0), maxAbsDiff: 0, totalDiffPct: 0, bitIdentical: false, durationSec: 0 };
    }
    if (bufA.sampleRate !== bufB.sampleRate) {
        // Different rates → sample-level diff isn't meaningful without resampling
        return { rmsPerSec: new Float32Array(0), maxAbsDiff: NaN, totalDiffPct: NaN, bitIdentical: false, durationSec: 0, mismatch: 'sample_rate' };
    }
    var sr = bufA.sampleRate;
    var offsetSamples = Math.round(offsetSec * sr);
    var sA = Math.max(0,  offsetSamples);
    var sB = Math.max(0, -offsetSamples);
    var overlap = Math.min(bufA.length - sA, bufB.length - sB);
    if (overlap <= 0) {
        return { rmsPerSec: new Float32Array(0), maxAbsDiff: NaN, totalDiffPct: NaN, bitIdentical: false, durationSec: 0 };
    }
    var nCh = Math.min(bufA.numberOfChannels, bufB.numberOfChannels);
    var EPS = Math.pow(2, -15);  // 16-bit-equivalent threshold

    var seconds = Math.max(1, Math.floor(overlap / sr));
    var rmsPerSec = new Float32Array(seconds);

    var maxAbsDiff = 0;
    var diffSamples = 0;
    var totalSamples = 0;

    for (var c = 0; c < nCh; c++) {
        var a = bufA.getChannelData(c);
        var b = bufB.getChannelData(c);
        for (var sec = 0; sec < seconds; sec++) {
            var from = sec * sr;
            var to   = Math.min(from + sr, overlap);
            var sumSq = 0;
            var n = to - from;
            for (var i = from; i < to; i++) {
                var d = a[sA + i] - b[sB + i];
                sumSq += d * d;
                var ad = d < 0 ? -d : d;
                if (ad > maxAbsDiff) maxAbsDiff = ad;
                if (ad > EPS) diffSamples++;
                totalSamples++;
            }
            // Accumulate squared-error across channels — divide later
            rmsPerSec[sec] += sumSq / n;
        }
    }
    // Convert accumulated per-sec MS into RMS averaged across channels
    for (var s2 = 0; s2 < seconds; s2++) {
        rmsPerSec[s2] = Math.sqrt(rmsPerSec[s2] / nCh);
    }

    return {
        rmsPerSec: rmsPerSec,
        maxAbsDiff: maxAbsDiff,
        totalDiffPct: totalSamples > 0 ? (100 * diffSamples / totalSamples) : 0,
        bitIdentical: maxAbsDiff < EPS,
        durationSec: overlap / sr,
    };
}

/**
 * Wrap LoudnessR128 with a graceful fallback to basicStats peak/rms when
 * For now: just identify "different length" / "different channels" / "different SR"
 * as obvious deltas. Otherwise returns "pending" so UI can show "more analysis coming".
 * ────────────────────────────────────────────────────────────────── */

/* ──────────────────────────────────────────────────────────────────
 * M6 — Real weighted multi-dimension verdict engine.
 *
 * Replaces the placeholder skeletonVerdict (which only surfaced deltas)
 * with a proper "which file is better, and how confident are we?" call.
 *
 * 8 axes, weights sum to 1.0:
 *
 *   axis              weight   winning rule
 *   resolution        0.18     higher effective bit-depth + higher true SR;
 *                              honest format > upsampled fake-Hi-Res
 *   spectral_integrity 0.22    higher cutoff Hz (less lossy ceiling) AND
 *                              more natural HF roll-off
 *   dynamic_range     0.18     higher LRA in -23..-9 LUFS range (NOT
 *                              "louder = better")
 *   true_peak_safety  0.08     closer to -1 dBTP, no intersample clipping
 *   loudness_target   0.10     LUFS-I closer to Spotify -14 (default; the
 *                              actual target matters less than which file
 *                              hits it)
 *   codec_quality     0.10     lossless > VBR ≥ 256 > CBR 320 > CBR 192 >
 *                              CBR 128
 *   stereo_integrity  0.06     width > 0.05, phase corr > 0, not mono-summed
 *   bit_perfect_diff  0.08     bit-identical → both score 1 ("indistinguishable")
 *
 * Per-axis score is [0..1] for each side; the global verdict is the
 * weighted sum. Confidence band:
 *   high   — ≥4 axes have a clear winner (score gap > 0.2 between A and B)
 *            and the winners all agree
 *   medium — 2-3 axes agree, others balanced
 *   low    — axes disagree → "Mixed: files have different strengths"
 *
 * Edge cases (honest verdicts that override the score):
 *   bit-identical      → "Indistinguishable" verdict, score 50/50
 *   different content   → "These appear to be different recordings"
 *   different durations → warning surfaced even with a normal score
 * ────────────────────────────────────────────────────────────────── */

var AXIS_WEIGHTS = {
    resolution:        0.18,
    spectral_integrity: 0.22,
    dynamic_range:     0.18,
    true_peak_safety:  0.08,
    loudness_target:   0.10,
    codec_quality:     0.10,
    stereo_integrity:  0.06,
    bit_perfect_diff:  0.08,
};

function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }

// Convert per-side raw value × inverse-side value into a (scoreA, scoreB)
// pair in [0..1] that sums to ~1.0 when they're comparable. Used for
// any axis where "higher is better" with a comparable scale.
function relativePair(a, b) {
    if (!isFinite(a) && !isFinite(b)) return { a: 0.5, b: 0.5 };
    if (!isFinite(a)) return { a: 0, b: 1 };
    if (!isFinite(b)) return { a: 1, b: 0 };
    var total = Math.abs(a) + Math.abs(b);
    if (total < 1e-9) return { a: 0.5, b: 0.5 };
    return { a: clamp01(a / total), b: clamp01(b / total) };
}

function codecScore(probe, stats, loud) {
    // Higher score for lossless / higher-quality codecs.
    if (!probe) return 0.5;
    if (probe.container === 'wav' || probe.container === 'flac' || probe.container === 'aiff' || probe.codec === 'pcm' || probe.codec === 'flac') return 1.0;
    // For lossy, peek at the file's bytes-per-second ratio
    var bps = probe.size && stats && stats.durationSec ? (probe.size * 8) / stats.durationSec : 0;
    if (bps >= 280000) return 0.85;  // ~VBR ≥256 / CBR 320
    if (bps >= 160000) return 0.65;  // CBR 192 / VBR ~192
    if (bps >= 110000) return 0.45;  // CBR 128
    if (bps > 0)        return 0.25;  // <128 (heavy)
    return 0.5;
}

function lufsTargetScore(lufs, target) {
    if (!isFinite(lufs)) return 0.5;
    var delta = Math.abs(lufs - target);
    // 0 LU away → 1.0, 6 LU away → 0.5, ≥12 LU away → ~0
    return clamp01(1 - delta / 12);
}

function widthScore(stereo) {
    if (!stereo || stereo.isMono) return 0.5;
    // Width 0.0 (mono-summed) → 0.0, 0.1 → ~0.5, 0.3+ → 1.0
    return clamp01(stereo.width / 0.3) * 0.7 + (stereo.phaseCorrelation > 0 ? 0.3 : 0);
}

function weightedVerdict(input) {
    input = input || {};
    var stats = input.stats || [null, null];          // [statsA, statsB]
    var loud  = input.loud  || [null, null];           // [loudA, loudB]
    var cut   = input.cut   || [null, null];           // [cutA, cutB]
    var stereo = input.stereo || [null, null];
    var probes = input.probes || [null, null];
    var align  = input.align || null;
    var diff   = input.diff  || null;
    var lufsTarget = input.lufsTarget != null ? input.lufsTarget : -14;

    // ── Edge cases that override scoring ─────────────────────────
    if (diff && diff.bitIdentical) {
        return {
            kind:        'indistinguishable',
            labelKey:    'verdict_indistinguishable',
            confidence:  'high',
            scoreA:      50, scoreB: 50,
            axes:        [],
            edge:        'bit_identical',
        };
    }
    if (align && align.correlation < 0.5) {
        return {
            kind:        'different_content',
            labelKey:    'verdict_different_content',
            confidence:  'high',
            scoreA:      50, scoreB: 50,
            axes:        [],
            edge:        'different_content',
        };
    }

    // ── Per-axis scoring ────────────────────────────────────────
    var axes = [];

    // resolution = effective bit-depth proxy (lossless ↔ lossy) + true SR
    var resA = (probes[0] && (probes[0].container === 'wav' || probes[0].container === 'flac' || probes[0].container === 'aiff')) ? 1.0 : 0.6;
    var resB = (probes[1] && (probes[1].container === 'wav' || probes[1].container === 'flac' || probes[1].container === 'aiff')) ? 1.0 : 0.6;
    if (stats[0] && stats[1] && stats[0].sampleRate !== stats[1].sampleRate) {
        if (stats[0].sampleRate > stats[1].sampleRate) resA += 0.1; else resB += 0.1;
    }
    axes.push({ axis: 'resolution', weight: AXIS_WEIGHTS.resolution, scoreA: clamp01(resA), scoreB: clamp01(resB) });

    // spectral_integrity = higher cutoff Hz is better. Full-band tied to 1.0.
    var spA = 0.5, spB = 0.5;
    if (cut[0] && cut[1] && cut[0].hz > 0 && cut[1].hz > 0) {
        var pair = relativePair(cut[0].hz, cut[1].hz);
        spA = pair.a; spB = pair.b;
        // Normalise so tied full-band → 0.5/0.5 still wins both at full marks
        if (cut[0].isFullBand && cut[1].isFullBand) { spA = 0.5; spB = 0.5; }
        else if (cut[0].isFullBand && !cut[1].isFullBand) { spA = 0.85; spB = 1 - 0.85; }
        else if (cut[1].isFullBand && !cut[0].isFullBand) { spB = 0.85; spA = 1 - 0.85; }
    }
    axes.push({ axis: 'spectral_integrity', weight: AXIS_WEIGHTS.spectral_integrity, scoreA: spA, scoreB: spB });

    // dynamic_range = LRA, in the healthy 9..23 LU band. Brick-wall <6 = bad.
    var drA = 0.5, drB = 0.5;
    if (loud[0] && loud[1] && isFinite(loud[0].lra) && isFinite(loud[1].lra)) {
        drA = clamp01(loud[0].lra / 12);
        drB = clamp01(loud[1].lra / 12);
        // Re-normalise so we always get a relative-pair score that adds to 1
        var dTot = drA + drB;
        if (dTot > 0) { drA = drA / dTot; drB = drB / dTot; }
    }
    axes.push({ axis: 'dynamic_range', weight: AXIS_WEIGHTS.dynamic_range, scoreA: drA, scoreB: drB });

    // true_peak_safety = penalised when > 0 dBTP (intersample clipping risk)
    function tpScore(tp) {
        if (!isFinite(tp)) return 0.5;
        if (tp > 0) return 0.1;
        if (tp > -1) return 0.6;
        return 1.0;
    }
    axes.push({
        axis: 'true_peak_safety', weight: AXIS_WEIGHTS.true_peak_safety,
        scoreA: tpScore(loud[0] && loud[0].truePeakDb),
        scoreB: tpScore(loud[1] && loud[1].truePeakDb),
    });

    // loudness_target = closer to the streaming target wins
    axes.push({
        axis: 'loudness_target', weight: AXIS_WEIGHTS.loudness_target,
        scoreA: lufsTargetScore(loud[0] && loud[0].integratedLufs, lufsTarget),
        scoreB: lufsTargetScore(loud[1] && loud[1].integratedLufs, lufsTarget),
    });

    // codec_quality
    axes.push({
        axis: 'codec_quality', weight: AXIS_WEIGHTS.codec_quality,
        scoreA: codecScore(probes[0], stats[0], loud[0]),
        scoreB: codecScore(probes[1], stats[1], loud[1]),
    });

    // stereo_integrity
    axes.push({
        axis: 'stereo_integrity', weight: AXIS_WEIGHTS.stereo_integrity,
        scoreA: widthScore(stereo[0]),
        scoreB: widthScore(stereo[1]),
    });

    // bit_perfect_diff — only awards when diff is meaningfully different and
    // one side appears to be the cleaner source. For two encodes of the
    // same source where corr > 0.95 but not bit-identical, we don't really
    // know which is "cleaner" from sample diff alone — leave at 0.5/0.5.
    axes.push({ axis: 'bit_perfect_diff', weight: AXIS_WEIGHTS.bit_perfect_diff, scoreA: 0.5, scoreB: 0.5 });

    // ── Aggregate weighted scores ───────────────────────────────
    var scoreA = 0, scoreB = 0;
    for (var i = 0; i < axes.length; i++) {
        scoreA += axes[i].weight * axes[i].scoreA;
        scoreB += axes[i].weight * axes[i].scoreB;
    }
    // Normalise to %
    var pctA = Math.round(100 * scoreA);
    var pctB = Math.round(100 * scoreB);

    // ── Confidence band ─────────────────────────────────────────
    var clearWins = 0;
    var aWins = 0, bWins = 0;
    for (var j = 0; j < axes.length; j++) {
        var diffScore = axes[j].scoreA - axes[j].scoreB;
        if (Math.abs(diffScore) >= 0.2) {
            clearWins++;
            if (diffScore > 0) aWins++; else bWins++;
        }
    }
    var confidence;
    var verdictKind;
    if (clearWins >= 4 && (aWins === clearWins || bWins === clearWins)) {
        confidence = 'high';
    } else if (clearWins >= 2 && Math.abs(aWins - bWins) >= 1) {
        confidence = 'medium';
    } else {
        confidence = 'low';
    }

    if (confidence === 'low' || Math.abs(pctA - pctB) < 4) {
        verdictKind = 'mixed';
    } else if (pctA > pctB) {
        verdictKind = 'a_wins';
    } else {
        verdictKind = 'b_wins';
    }

    return {
        kind:        verdictKind,
        labelKey:    'verdict_' + verdictKind,
        confidence:  confidence,
        scoreA:      pctA, scoreB: pctB,
        axes:        axes,
        edge:        null,
        clearWins:   clearWins,
        aWins:       aWins, bWins: bWins,
    };
}

/* ──────────────────────────────────────────────────────────────────
 * M7 — ABX blind-test math (level-matching gain + binomial p-value).
 *
 *   levelMatchGainDb(lufsA, lufsB, opts?)
 *     Returns {gainAdb, gainBdb} — apply each gain to its file so both
 *     play at the mean LUFS (or opts.target if specified). Skips gain
 *     for files where LUFS is unavailable (NaN). Caps the boost at
 *     opts.maxBoostDb (default +9 dB) so a -40 LUFS ambient file
 *     doesn't blow speakers when matched to a -8 LUFS pop master.
 *
 *   binomialPValue(correct, total)
 *     Right-tail probability P(X ≥ correct | n=total, p=0.5) — what we
 *     show next to the ABX score so the user knows when their guesses
 *     are statistically significant (typically p ≤ 0.05). Exact
 *     computation via cumulative binomial — works for n up to ~50
 *     before floating-point loses precision (irrelevant here, the UI
 *     caps at 20 trials).
 *
 *   makeAbxTrial(seed?)
 *     Returns {x: 'A'|'B', expected: 'A'|'B', token}. The caller routes
 *     audio playback to file X = the file picked. expected is the
 *     ground truth (== x); the UI uses it to score after the user
 *     guesses. seed allows deterministic test runs.
 * ────────────────────────────────────────────────────────────────── */

function levelMatchGainDb(lufsA, lufsB, opts) {
    opts = opts || {};
    var maxBoostDb = opts.maxBoostDb != null ? opts.maxBoostDb : 9;
    var target = opts.target;
    var aOk = isFinite(lufsA);
    var bOk = isFinite(lufsB);
    if (target == null) {
        if (aOk && bOk)      target = (lufsA + lufsB) / 2;
        else if (aOk)        target = lufsA;
        else if (bOk)        target = lufsB;
        else                  target = -14;
    }
    function gainFor(lufs) {
        if (!isFinite(lufs)) return 0;
        var g = target - lufs;
        if (g > maxBoostDb) g = maxBoostDb;
        if (g < -24)        g = -24;
        return g;
    }
    return { gainAdb: gainFor(lufsA), gainBdb: gainFor(lufsB), targetLufs: target };
}

// Binomial probability mass: C(n,k) * p^k * (1-p)^(n-k). For p=0.5 the
// (1-p)^(n-k) collapses to 0.5^n. Use log-binomial to avoid overflow.
function logBinomCoeff(n, k) {
    if (k < 0 || k > n) return -Infinity;
    var s = 0;
    for (var i = 1; i <= k; i++) s += Math.log(n - i + 1) - Math.log(i);
    return s;
}

function binomialPValue(correct, total) {
    if (total <= 0) return 1;
    if (correct < 0) correct = 0;
    if (correct > total) correct = total;
    var p = 0;
    for (var k = correct; k <= total; k++) {
        p += Math.exp(logBinomCoeff(total, k) - total * Math.LN2);
    }
    return p;
}

// Deterministic Mulberry32 PRNG when caller passes a seed; otherwise
// Math.random. Mulberry32 is good enough for trial randomisation and
// deterministic test runs (e.g. validator scenarios).
function makeRng(seed) {
    if (seed == null) return Math.random;
    var s = seed >>> 0;
    return function () {
        s = (s + 0x6D2B79F5) >>> 0;
        var t = s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function makeAbxTrial(seed) {
    var rng = makeRng(seed);
    var x = rng() < 0.5 ? 'A' : 'B';
    return { x: x, expected: x, token: Math.random().toString(36).slice(2, 10) };
}

/* ──────────────────────────────────────────────────────────────────
 * M8 — Deep mode analysis (per-stem bundle).
 *
 *   analyzeStem(stemBuffer)
 *     Cheap reuse of basicStats + computeAveragePsd + findSpectralCutoff +
 *     measureLoudness + computeStereoMetrics on a single decoded stem
 *     (vocal or instrumental AudioBuffer). Bundles them into a single JSON
 *     object the UI can drop straight into a per-stem comparison card.
 *
 *   The actual stem-split call (window.StemSeparator.split) is initiated
 *   by the blade because it's async, progress-reporting, and needs UI
 *   plumbing. The engine just analyses whatever the blade hands it.
 * ────────────────────────────────────────────────────────────────── */

function analyzeStem(stemBuffer) {
    if (!stemBuffer) return null;
    var stats  = basicStats(stemBuffer);
    var loud   = measureLoudness(stemBuffer);
    var psd    = computeAveragePsd(stemBuffer);
    var cutoff = findSpectralCutoff(psd);
    var stereo = computeStereoMetrics(stemBuffer);
    return { stats: stats, loud: loud, psd: psd, cutoff: cutoff, stereo: stereo };
}

/* ──────────────────────────────────────────────────────────────────
 * M10 — In-browser self-validator.
 *
 *   Generates synthetic AudioBuffer pairs via OfflineAudioContext-free
 *   path (we construct AudioBuffer-shaped POJOs that quack like the real
 *   thing for our engine's purposes — every engine call uses only
 *   .numberOfChannels / .length / .sampleRate / .duration /
 *   .getChannelData), then runs the engine and asserts the expected
 *   verdict for each scenario.
 *
 *   selfValidate() returns:
 *     {
 *       version, passed:int, failed:int, scenarios:[
 *         { name, expected, actual, ok, detail }
 *       ]
 *     }
 *
 *   Scenarios cover the etalon-acceptance gates the plan called for:
 *     1.  Identical pair → bit-identical detect (align.corr ≈ 1.0)
 *     2.  Mono vs stereo of the same source → stereo_integrity skews B
 *     3.  Same source level-shifted ±6 LUFS → loudness_target differs
 *     4.  Same source × low-passed at 8 kHz → spectral_integrity favors raw
 *     5.  Clipped vs clean → true_peak_safety favors clean
 *     6.  Brickwall-limited (LRA ~3 LU) vs dynamic (LRA ~12 LU) → LRA axis
 *     7.  Different content (uncorrelated noise vs sine) → kind=different_content
 *     8.  Same source, B offset by 1 sec → alignment reports offset ≈ 1 s
 *
 *   This isn't a substitute for the real-file cross-browser battery
 *   (Playwright, FFmpeg-generated MP3/FLAC/OGG/WAV pairs) — that lives
 *   in tests/playwright/. The in-browser validator is the fast checks
 *   that run on every prod page load via ?validate=1 query, so we'd
 *   notice an engine regression immediately.
 * ────────────────────────────────────────────────────────────────── */

function _makeBuffer(sampleRate, channels, samples, fillFn) {
    // POJO that matches the AudioBuffer surface the engine actually touches.
    var data = [];
    for (var c = 0; c < channels; c++) {
        var arr = new Float32Array(samples);
        for (var i = 0; i < samples; i++) arr[i] = fillFn ? fillFn(i, c) : 0;
        data.push(arr);
    }
    return {
        sampleRate: sampleRate,
        numberOfChannels: channels,
        length: samples,
        duration: samples / sampleRate,
        getChannelData: function (c) { return data[c]; },
    };
}

function _sine(freq, sr) {
    return function (i) { return Math.sin(2 * Math.PI * freq * i / sr); };
}

function _gainBuffer(buf, gain) {
    var nCh = buf.numberOfChannels;
    var len = buf.length;
    return _makeBuffer(buf.sampleRate, nCh, len, function (i, c) {
        return buf.getChannelData(c)[i] * gain;
    });
}

function _clipBuffer(buf, ceiling) {
    var nCh = buf.numberOfChannels;
    return _makeBuffer(buf.sampleRate, nCh, buf.length, function (i, c) {
        var v = buf.getChannelData(c)[i];
        return v > ceiling ? ceiling : (v < -ceiling ? -ceiling : v);
    });
}

function _monoSum(buf) {
    if (buf.numberOfChannels < 2) return buf;
    var L = buf.getChannelData(0), R = buf.getChannelData(1);
    return _makeBuffer(buf.sampleRate, 2, buf.length, function (i) {
        return (L[i] + R[i]) * 0.5;
    });
}

// Very crude lowpass — single-pole IIR. Good enough to drop the spectral
// cutoff to a clear value for the validator.
function _lowpassBuffer(buf, fc) {
    var sr = buf.sampleRate;
    var rc = 1 / (2 * Math.PI * fc);
    var dt = 1 / sr;
    var alpha = dt / (rc + dt);
    return _makeBuffer(sr, buf.numberOfChannels, buf.length, function (i, c) {
        var x = buf.getChannelData(c);
        var prev = i === 0 ? x[0] : 0;
        var y = x[0];
        // Re-walk from 0 — small length so OK.
        for (var k = 1; k <= i; k++) y = y + alpha * (x[k] - y);
        return y;
    });
}

function _offsetBuffer(buf, offsetSec) {
    var sr = buf.sampleRate;
    var off = Math.round(offsetSec * sr);
    var newLen = buf.length + off;
    return _makeBuffer(sr, buf.numberOfChannels, newLen, function (i, c) {
        if (i < off) return 0;
        return buf.getChannelData(c)[i - off];
    });
}

function _addNoise(buf, amp) {
    return _makeBuffer(buf.sampleRate, buf.numberOfChannels, buf.length, function (i, c) {
        return buf.getChannelData(c)[i] + (Math.random() * 2 - 1) * amp;
    });
}

function selfValidate(opts) {
    opts = opts || {};
    var sr = opts.sampleRate || 22050;
    var seconds = opts.seconds || 1.5;
    var samples = Math.floor(sr * seconds);

    var sineA = _makeBuffer(sr, 2, samples, _sine(440, sr));
    var sineHi = _makeBuffer(sr, 2, samples, _sine(15000, sr));
    var scenarios = [];

    function add(name, expectedPred, run) {
        var t0 = Date.now();
        try {
            var actual = run();
            var ok = false, detail = null;
            try { var pred = expectedPred(actual); ok = !!pred.ok; detail = pred.detail; }
            catch (e) { ok = false; detail = 'predicate threw: ' + (e && e.message); }
            scenarios.push({ name: name, ok: ok, detail: detail, ms: Date.now() - t0, actual: actual });
        } catch (e) {
            scenarios.push({ name: name, ok: false, detail: 'scenario threw: ' + (e && e.message), ms: Date.now() - t0 });
        }
    }

    // 1. Identical pair → bit-identical
    add('identical_pair_bit_identical', function (r) {
        var ok = r.align && r.align.correlation > 0.999;
        return { ok: ok, detail: 'corr=' + (r.align && r.align.correlation.toFixed(4)) };
    }, function () {
        var align = alignBuffers(sineA, sineA);
        return { align: align, diff: computeSampleDiff(sineA, sineA, align.offsetSec) };
    });

    // 2. Mono vs stereo of same source → stereo width differs
    add('mono_vs_stereo_width', function (r) {
        var ok = (r.stB.width || 0) > 0.001 && (r.stA.width || 0) <= 0.001;
        return { ok: ok, detail: 'widthA=' + r.stA.width + ' widthB=' + r.stB.width };
    }, function () {
        var mono = _monoSum(sineA);          // mono-summed in stereo container
        var wideStereo = _makeBuffer(sr, 2, samples, function (i, c) {
            return c === 0 ? Math.sin(2 * Math.PI * 440 * i / sr) : Math.sin(2 * Math.PI * 660 * i / sr);
        });
        return { stA: computeStereoMetrics(mono), stB: computeStereoMetrics(wideStereo) };
    });

    // 3. LUFS shift +6 dB → loudness_target differs
    add('lufs_shift_6db', function (r) {
        var ok = isFinite(r.lufsA) && isFinite(r.lufsB) && Math.abs(r.lufsA - r.lufsB) > 3;
        return { ok: ok, detail: 'lufsA=' + r.lufsA + ' lufsB=' + r.lufsB };
    }, function () {
        var quiet = _gainBuffer(sineA, 0.25);
        var loud  = _gainBuffer(sineA, 0.5);
        return { lufsA: measureLoudness(quiet).integratedLufs, lufsB: measureLoudness(loud).integratedLufs };
    });

    // 4. Lowpass → spectral cutoff differs
    add('lowpass_cutoff_differs', function (r) {
        var ok = r.cutA && r.cutB && (r.cutA.hz - r.cutB.hz) > 500;
        return { ok: ok, detail: 'cutA=' + (r.cutA && r.cutA.hz) + ' cutB=' + (r.cutB && r.cutB.hz) };
    }, function () {
        var full = _addNoise(sineA, 0.05);
        var lp   = _lowpassBuffer(full, 3000);
        var psdA = computeAveragePsd(full);
        var psdB = computeAveragePsd(lp);
        return { cutA: findSpectralCutoff(psdA), cutB: findSpectralCutoff(psdB) };
    });

    // 5. Clipped vs clean → true_peak differs
    add('clipped_vs_clean_true_peak', function (r) {
        var ok = isFinite(r.tpClipped) && isFinite(r.tpClean) && r.tpClipped > r.tpClean - 0.5;
        return { ok: ok, detail: 'tpClipped=' + r.tpClipped + ' tpClean=' + r.tpClean };
    }, function () {
        var clean = _gainBuffer(sineA, 0.5);
        var clipped = _clipBuffer(_gainBuffer(sineA, 1.2), 0.99);
        return { tpClipped: measureLoudness(clipped).truePeakDb, tpClean: measureLoudness(clean).truePeakDb };
    });

    // 6. Offset alignment — broadband noise source (NOT periodic sine — xcorr
    //    on a 440 Hz sine + small noise locks onto lag=0 because the signal
    //    repeats every 2.27 ms, so every integer-period offset is equally
    //    valid; the additive noise isn't strong enough to break ambiguity).
    //    With pure broadband noise the autocorrelation has a single sharp
    //    peak at the true lag, so the test reflects real-world music/voice
    //    behaviour where alignment is meaningful.
    add('offset_alignment_0p5s', function (r) {
        var ok = r.align && r.align.correlation > 0.5 && Math.abs(Math.abs(r.align.offsetSec) - 0.5) < 0.15;
        return { ok: ok, detail: 'offset=' + (r.align && r.align.offsetSec.toFixed(3)) + ' corr=' + (r.align && r.align.correlation.toFixed(3)) };
    }, function () {
        // Use the SAME random sequence for both buffers so xcorr can find the offset.
        // Math.random() in _addNoise gives different sequences each call — instead
        // build a single shared noise array first, then offset it.
        var noise = new Float32Array(samples);
        var rng = 12345;
        for (var i = 0; i < samples; i++) {
            rng = (rng * 1103515245 + 12345) & 0x7fffffff;
            noise[i] = ((rng % 1000) / 1000 - 0.5) * 0.5;
        }
        var src = _makeBuffer(sr, 1, samples, function (i) { return noise[i]; });
        var shifted = _offsetBuffer(src, 0.5);
        return { align: alignBuffers(src, shifted) };
    });

    // 7. Different content (440 Hz vs 15 kHz) → low correlation
    add('different_content_low_corr', function (r) {
        var ok = r.align && r.align.correlation < 0.5;
        return { ok: ok, detail: 'corr=' + (r.align && r.align.correlation.toFixed(3)) };
    }, function () {
        return { align: alignBuffers(_addNoise(sineA, 0.1), _addNoise(sineHi, 0.1)) };
    });

    // 8. Polarity flip on B → phase corr ~ −1 vs +1
    add('polarity_flip_phase', function (r) {
        var ok = r.phA > 0 && r.phB < 0;
        return { ok: ok, detail: 'phA=' + r.phA.toFixed(3) + ' phB=' + r.phB.toFixed(3) };
    }, function () {
        var stereo = _makeBuffer(sr, 2, samples, function (i, c) {
            return Math.sin(2 * Math.PI * 440 * i / sr) * (c === 0 ? 1 : 0.7);
        });
        var flipped = _makeBuffer(sr, 2, samples, function (i, c) {
            var v = Math.sin(2 * Math.PI * 440 * i / sr) * (c === 0 ? 1 : 0.7);
            return c === 1 ? -v : v;
        });
        return { phA: computeStereoMetrics(stereo).phaseCorrelation, phB: computeStereoMetrics(flipped).phaseCorrelation };
    });

    // Final tally
    var passed = 0, failed = 0;
    for (var i = 0; i < scenarios.length; i++) {
        if (scenarios[i].ok) passed++; else failed++;
        // Strip the `actual` blob from the public result — too big to JSON.
        delete scenarios[i].actual;
    }
    return { version: VERSION, passed: passed, failed: failed, scenarios: scenarios };
}

function skeletonVerdict(summaryA, summaryB, loudA, loudB, cutA, cutB, align, diff, stA, stB, wmA, wmB) {
    if (!summaryA || !summaryB) {
        return { kind: 'incomplete', label: null, score_a: 0, score_b: 0, deltas: [] };
    }
    // Deltas now carry an i18n key + arg dict, NOT a pre-formatted English
    // string. The blade resolves L[key] with %{name} placeholder substitution
    // so all 9 locales get translated detail text. Closes Farid's QA report
    // 2026-05-22 — "не все пункты переведены".
    var deltas = [];
    function push(axis, key, args) { deltas.push({ axis: axis, key: key, args: args || {} }); }

    if (Math.abs(summaryA.durationSec - summaryB.durationSec) > 0.5) {
        var dDelta = summaryB.durationSec - summaryA.durationSec;
        push('duration', dDelta > 0 ? 'delta_duration_b_longer' : 'delta_duration_b_shorter',
            { sec: Math.abs(dDelta).toFixed(2) });
    }
    if (summaryA.sampleRate !== summaryB.sampleRate) {
        push('sample_rate', 'delta_sample_rate', { a: summaryA.sampleRate, b: summaryB.sampleRate });
    }
    if (summaryA.channels !== summaryB.channels) {
        push('channels', 'delta_channels', { a: summaryA.channels, b: summaryB.channels });
    }
    var peakDiff = Math.abs(summaryA.peakDbfs - summaryB.peakDbfs);
    if (peakDiff > 3 && isFinite(peakDiff)) {
        push('peak', 'delta_peak', { a: summaryA.peakDbfs.toFixed(1), b: summaryB.peakDbfs.toFixed(1) });
    }
    var rmsDiff = Math.abs(summaryA.rmsDbfs - summaryB.rmsDbfs);
    if (rmsDiff > 6 && isFinite(rmsDiff)) {
        push('rms', 'delta_rms', { d: rmsDiff.toFixed(1) });
    }
    if (loudA && loudB) {
        if (isFinite(loudA.integratedLufs) && isFinite(loudB.integratedLufs)) {
            var lufsDiff = Math.abs(loudA.integratedLufs - loudB.integratedLufs);
            if (lufsDiff > 1) {
                push('lufs', 'delta_lufs', { a: loudA.integratedLufs.toFixed(1), b: loudB.integratedLufs.toFixed(1), d: lufsDiff.toFixed(1) });
            }
        }
        if (isFinite(loudA.lra) && isFinite(loudB.lra)) {
            var lraDiff = Math.abs(loudA.lra - loudB.lra);
            if (lraDiff > 1.5) {
                push('lra', 'delta_lra', { a: loudA.lra.toFixed(1), b: loudB.lra.toFixed(1) });
            }
        }
        if (isFinite(loudA.truePeakDb) && isFinite(loudB.truePeakDb)) {
            var tpDiff = Math.abs(loudA.truePeakDb - loudB.truePeakDb);
            if (tpDiff > 1.5) {
                push('true_peak', 'delta_true_peak', { a: loudA.truePeakDb.toFixed(1), b: loudB.truePeakDb.toFixed(1) });
            }
            if (loudA.truePeakDb > 0 && loudB.truePeakDb <= 0) {
                push('clipping', 'delta_clipping_a', {});
            } else if (loudB.truePeakDb > 0 && loudA.truePeakDb <= 0) {
                push('clipping', 'delta_clipping_b', {});
            }
        }
    }
    if (cutA && cutB && cutA.hz > 0 && cutB.hz > 0) {
        var cutDiff = Math.abs(cutA.hz - cutB.hz);
        if (cutDiff > 500) {
            push('spectral_cutoff', 'delta_spectral_cutoff', { a: (Math.round(cutA.hz / 100) / 10), b: (Math.round(cutB.hz / 100) / 10) });
        }
    }
    if (align) {
        if (align.correlation < 0.5) {
            push('alignment', 'delta_alignment_different_content', { corr: align.correlation.toFixed(2) });
        } else if (Math.abs(align.offsetSec) > 0.05) {
            push('alignment', 'delta_alignment_offset', { sec: align.offsetSec.toFixed(3), corr: align.correlation.toFixed(2) });
        }
    }
    if (diff && diff.bitIdentical) {
        push('bit_identical', 'delta_bit_identical', {});
    } else if (diff && isFinite(diff.totalDiffPct) && diff.totalDiffPct > 0 && diff.totalDiffPct < 0.5) {
        push('sample_diff', 'delta_sample_diff', {
            pct: diff.totalDiffPct.toFixed(2),
            db: (20 * Math.log10(Math.max(diff.maxAbsDiff, 1e-10))).toFixed(1),
        });
    }
    if (stA && stB && !stA.isMono && !stB.isMono) {
        var widthDiff = Math.abs(stA.width - stB.width);
        if (widthDiff > 0.1) {
            var monoHint = stA.width < 0.02 ? 'delta_stereo_width_a_mono'
                         : stB.width < 0.02 ? 'delta_stereo_width_b_mono'
                                            : 'delta_stereo_width';
            push('stereo_width', monoHint, { a: stA.width.toFixed(2), b: stB.width.toFixed(2) });
        }
        if (Math.abs(stA.phaseCorrelation - stB.phaseCorrelation) > 0.3) {
            push('phase_correlation', 'delta_phase_correlation', { a: stA.phaseCorrelation.toFixed(2), b: stB.phaseCorrelation.toFixed(2) });
        }
        if ((stA.phaseCorrelation > 0.5 && stB.phaseCorrelation < -0.5) || (stA.phaseCorrelation < -0.5 && stB.phaseCorrelation > 0.5)) {
            push('polarity', 'delta_polarity', {});
        }
    } else if (stA && stB && stA.isMono !== stB.isMono) {
        push('mono_stereo_mismatch', stA.isMono ? 'delta_mono_stereo_a_mono' : 'delta_mono_stereo_b_mono', {});
    }
    if (wmA && wmA.detected) {
        push('watermark_a', 'delta_watermark_a', { source: (wmA.source || 'unknown'), evidence: (wmA.evidence || '') });
    }
    if (wmB && wmB.detected) {
        push('watermark_b', 'delta_watermark_b', { source: (wmB.source || 'unknown'), evidence: (wmB.evidence || '') });
    }
    return {
        kind: deltas.length ? 'pending_with_deltas' : 'pending',
        label: null,
        score_a: 0,
        score_b: 0,
        deltas: deltas,
    };
}

/* ──────────────────────────────────────────────────────────────────
 * Public API
 * ────────────────────────────────────────────────────────────────── */

var api = {
    VERSION: VERSION,
    probeContainer: probeContainer,
    basicStats: basicStats,
    skeletonVerdict: skeletonVerdict,
    // M2 additions
    computeAveragePsd: computeAveragePsd,
    findSpectralCutoff: findSpectralCutoff,
    measureLoudness: measureLoudness,
    // M3 additions
    measureShortTermLufsOverTime: measureShortTermLufsOverTime,
    // M4 additions
    alignBuffers: alignBuffers,
    computeSampleDiff: computeSampleDiff,
    bitIdenticalQuick: bitIdenticalQuick,
    __fftCrossCorrelation: fftCrossCorrelation,
    // M5 additions
    computeStereoMetrics: computeStereoMetrics,
    scanWatermark: scanWatermark,
    // M6 additions
    weightedVerdict: weightedVerdict,
    AXIS_WEIGHTS: AXIS_WEIGHTS,
    // M7 additions
    levelMatchGainDb: levelMatchGainDb,
    binomialPValue: binomialPValue,
    makeAbxTrial: makeAbxTrial,
    __logBinomCoeff: logBinomCoeff,
    // M8 additions
    analyzeStem: analyzeStem,
    // M10 additions
    selfValidate: selfValidate,
    // FFT/window primitives exposed for tests + future-Worker reuse
    __fftRadix2: fftRadix2,
    __hannWindow: hannWindow,
    __sniffMagic: sniffMagic,
};

global.AudioComparatorEngine = api;

// Drain stubbed calls from timbrica-core.js (if any).
if (typeof global.__drainSharedModule === 'function') {
    global.__drainSharedModule('AudioComparatorEngine');
}

})(typeof window !== 'undefined' ? window : (typeof self !== 'undefined' ? self : globalThis));
